/**
 * `RankedBarChart` — Amendment 1 §3.3 step 9's ranked results chart: watersheds ordered by
 * yield, each row's bar sized by its own yield, with three small columns beside it for
 * NDVI (peak), rainfall and SOC — no correlation coefficient anywhere near it, since the
 * ranking itself is the whole statement (`uc3/diagnosis`'s own `statements[]`, printed
 * beside this chart, not inside it). Hand-built SVG, the same register `HistogramChart`/
 * `SeriesChart` already use (the repo forbids chart libraries, plan Global Constraints).
 *
 * Fix round 1: the `aria-label` readout carries every number with its own unit (kg/ha, NDVI,
 * mm, g/kg — `uc3/diagnosis`'s own factor units) and the run's season/year, per the
 * presentation standard's "numbers always with unit and period".
 */
import { fmtNum } from '../../../lib/format';
import type { IntelDiagnosisRankedRow } from '../../../lib/api';

export const RB_W = 360;
export const RB_ROW_H = 22;
export const RB_ROW_GAP = 10;
export const RB_LABEL_W = 96;
export const RB_YIELD_W = 120;
export const RB_COL_W = 34;
export const RB_COL_GAP = 6;
export const RB_PAD_TOP = 8;
export const RB_MIN_H = 60;

const plotX = RB_LABEL_W;
const pitch = RB_ROW_H + RB_ROW_GAP;

export interface RankedBarChartProps {
  rows: IntelDiagnosisRankedRow[];
  /** The run's own period — `uc3/diagnosis.season`/`.year` — read into the aria readout. */
  season?: string;
  year?: number;
  yieldLabel: string;
  /** GAEZ round: the modelled-yield column — "modelled (FAO GAEZ v5, 2020)". */
  modelledLabel: string;
  ndviLabel: string;
  rainLabel: string;
  socLabel: string;
  /** `uc3/diagnosis`'s own factor units — default to the values the live payload carries. */
  yieldUnit?: string;
  modelledUnit?: string;
  ndviUnit?: string;
  rainUnit?: string;
  socUnit?: string;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
}

function maxOf(
  rows: IntelDiagnosisRankedRow[],
  key: 'yield' | 'modelled_yield' | 'ndvi_peak' | 'rain' | 'soc',
): number {
  const values = rows.map((r) => r[key]).filter((v): v is number => typeof v === 'number');
  return values.length ? Math.max(...values) : 1;
}

/**
 * The chart's own `aria-label` — a plain-text readout of every row (each number with its own
 * unit) plus the run's period, for §I5 and the test list.
 */
function readout(
  rows: IntelDiagnosisRankedRow[],
  labels: { yield: string; modelled: string; ndvi: string; rain: string; soc: string },
  units: { yield: string; modelled: string; ndvi: string; rain: string; soc: string },
  season?: string,
  year?: number,
): string {
  const withUnit = (v: number | null, unit: string, digits: number) =>
    v == null ? '—' : `${fmtNum(v, digits)} ${unit}`;
  const period = season || year ? `${[season, year].filter(Boolean).join(', ')}. ` : '';
  const rowLines = rows
    .map(
      (row) =>
        `${row.rank}. ${row.name}: ${labels.yield} ${withUnit(row.yield, units.yield, 0)}, ` +
        `${labels.modelled} ${withUnit(row.modelled_yield, units.modelled, 1)}, ` +
        `${labels.ndvi} ${withUnit(row.ndvi_peak, units.ndvi, 0)}, ` +
        `${labels.rain} ${withUnit(row.rain, units.rain, 0)}, ` +
        `${labels.soc} ${withUnit(row.soc, units.soc, 1)}`,
    )
    .join('. ');
  return `${period}${rowLines}`;
}

export function RankedBarChart({
  rows,
  season,
  year,
  yieldLabel,
  modelledLabel,
  ndviLabel,
  rainLabel,
  socLabel,
  yieldUnit = 'kg/ha',
  modelledUnit = 't/ha',
  ndviUnit = 'NDVI',
  rainUnit = 'mm',
  socUnit = 'g/kg',
  selectedId,
  onSelect,
}: RankedBarChartProps) {
  const h = Math.max(RB_MIN_H, RB_PAD_TOP + Math.max(rows.length, 1) * pitch);
  const maxYield = maxOf(rows, 'yield');
  const maxModelled = maxOf(rows, 'modelled_yield');
  const maxNdvi = maxOf(rows, 'ndvi_peak');
  const maxRain = maxOf(rows, 'rain');
  const maxSoc = maxOf(rows, 'soc');
  const cols: {
    key: 'modelled_yield' | 'ndvi_peak' | 'rain' | 'soc';
    max: number;
    label: string;
    color: string;
  }[] = [
    { key: 'modelled_yield', max: maxModelled, label: modelledLabel, color: 'var(--plum, var(--ochre))' },
    { key: 'ndvi_peak', max: maxNdvi, label: ndviLabel, color: 'var(--brand)' },
    { key: 'rain', max: maxRain, label: rainLabel, color: 'var(--blue)' },
    { key: 'soc', max: maxSoc, label: socLabel, color: 'var(--ochre)' },
  ];

  return (
    <svg
      className="intel-chart rb-chart"
      viewBox={`0 0 ${RB_W} ${h}`}
      role="img"
      aria-label={readout(
        rows,
        { yield: yieldLabel, modelled: modelledLabel, ndvi: ndviLabel, rain: rainLabel, soc: socLabel },
        { yield: yieldUnit, modelled: modelledUnit, ndvi: ndviUnit, rain: rainUnit, soc: socUnit },
        season,
        year,
      )}
      data-testid="ranked-bar-chart"
    >
      {rows.map((row, i) => {
        const y = RB_PAD_TOP + i * pitch;
        const w = row.yield == null ? 0 : Math.max(2, (row.yield / maxYield) * RB_YIELD_W);
        const selected = row.unit_id === selectedId;
        return (
          <g
            key={row.unit_id}
            className="rb-row"
            data-unit={row.unit_id}
            style={onSelect ? { cursor: 'pointer' } : undefined}
            onClick={onSelect ? () => onSelect(row.unit_id) : undefined}
          >
            <title>
              {`${row.rank}. ${row.name} — ${yieldLabel} ${row.yield == null ? '—' : `${fmtNum(row.yield, 0)} ${yieldUnit}`}`}
            </title>
            <text className="rb-rowlabel" x={plotX - 10} y={y + RB_ROW_H / 2 + 4} textAnchor="end">
              {`${row.rank}. ${row.name}`}
            </text>
            {w > 0 ? (
              <rect
                x={plotX}
                y={y}
                width={w}
                height={RB_ROW_H}
                rx={3}
                fill="var(--brand-dark, var(--brand))"
                opacity={selected ? 1 : 0.78}
                stroke={selected ? 'var(--ink)' : 'none'}
                strokeWidth={selected ? 1.5 : 0}
              />
            ) : null}
            <text className="rb-num" x={plotX + w + 6} y={y + RB_ROW_H / 2 + 4}>
              {row.yield == null ? '—' : fmtNum(row.yield, 0)}
            </text>
            {cols.map((col, ci) => {
              const v = row[col.key];
              const colX = plotX + RB_YIELD_W + 40 + ci * (RB_COL_W + RB_COL_GAP);
              const barW = v == null ? 0 : Math.max(2, (v / col.max) * RB_COL_W);
              return (
                <g key={col.key}>
                  {barW > 0 ? (
                    <rect x={colX} y={y + 3} width={barW} height={RB_ROW_H - 6} rx={2} fill={col.color} opacity={0.7} />
                  ) : null}
                </g>
              );
            })}
          </g>
        );
      })}
      {/* Column headers, once, above the first row. */}
      {cols.map((col, ci) => (
        <text
          key={col.key}
          x={plotX + RB_YIELD_W + 40 + ci * (RB_COL_W + RB_COL_GAP)}
          y={RB_PAD_TOP - 2}
          className="rb-collabel"
          fontSize="7"
        >
          {col.label}
        </text>
      ))}
    </svg>
  );
}

export default RankedBarChart;
